import { IDBPDatabase } from 'idb'

import { ITableName, IDBSchema } from './types'

export default class PersistenceItem<Schema> {
  private storeName: ITableName
  private db: IDBPDatabase<IDBSchema>

  constructor(name: ITableName, db: IDBPDatabase<IDBSchema>) {
    this.storeName = name
    this.db = db
  }

  /**
   * Returns a cursor to this store
   */
  cursor = () => {
    return this.db.transaction(this.storeName).store.openCursor()
  }

  /**
   * Returns a transaction to this store
   */
  transaction = (mode?: 'readwrite') => {
    return this.db.transaction(this.storeName, mode)
  }

  /**
   * Get all items from this store. Optionally, filter by project ID (since it is very common to do so)
   */
  getAll = async ({ projectId }: { projectId?: string } = {}): Promise<
    Schema[]
  > => {
    const items = await this.db.getAllFromIndex(this.storeName, 'createdAt')
    if (projectId) {
      return items.filter(i => i.projectId === projectId)
    }

    return items
  }

  /**
   * Create one or more items in this store
   */
  create = async (data: Schema): Promise<void> => {
    try {
      await this.db.put(this.storeName, {
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
    } catch (e) {
      console.log('Error during PersistenceItem.create', e, this.storeName)
    }
  }

  /**
   * Update a single item in this store, by ID
   */
  update = async (id: string, data: Partial<Schema>): Promise<void> => {
    try {
      const record = await this.db.get(this.storeName, id)
      await this.db.put(this.storeName, {
        ...record,
        updatedAt: new Date().toISOString(),
      })
    } catch (e) {
      console.log('Error during PersistenceItem.update', e, this.storeName)
    }
  }

  /**
   * Remove a single item from this store, by ID
   */
  delete = async (key: string | string[]): Promise<void> => {
    try {
      await this.db.delete(this.storeName, key)
    } catch (e) {
      console.log('Error during PersistenceItem.delete', e, this.storeName)
    }
  }

  /**
   * Clear this store
   */
  clear = async () => {
    try {
      await this.db.clear(this.storeName)
    } catch (e) {
      console.log('Error during PersistenceItem.clear', e, this.storeName)
    }
  }
}
